import React, { useContext } from 'react';
import {
    AddOrEditPatient,
    CalenderOrAppointment,
    PatientsHome,
    Statistics,
    AdvancedSearch,
    ManageForms,
    ManageUsers,
    ManageClinics,
    Drug,
    DynamicForms,
    Laboratory,
    Radiology,
    Profile,
    File,
    Appointment
} from '../pages';
import PatientRoutes from './PatientRoutes';
import SettingRoutes from './SettingRoutes';
import MessagesContext from '../contexts/messagesContext';
import appoitmentIcon from '../assets/images/Icon/sideBar/appointment.svg';
import drugsIcon from '../assets/images/Icon/sideBar/drugs.svg';
import laboratoryIcon from '../assets/images/Icon/sideBar/laboratory.svg';
import profileIcon from '../assets/images/Icon/sideBar/profile.svg';
import radiologyIcon from '../assets/images/Icon/sideBar/radiology.svg';
import visitIcon from '../assets/images/Icon/sideBar/visit.svg';
import listOfPatientsIcon from '../assets/images/logo/users-icon-side-right.svg';
import calenderIcon from '../assets/images/logo/calender-icon-side-right.svg';
import settingIcon from '../assets/images/logo/setting-icon-side-bar-right.svg';
import search from '../assets/images/logo/sideBarSearch.svg';
import statistics from '../assets/images/logo/statistics.svg';
import formsIcon from '../assets/images/Icon/settingSideBar/formSetting.svg';
import manageUsersIcon from '../assets/images/Icon/settingSideBar/manageUsers.svg';
import manageClinicsIcon from '../assets/images/Icon/settingSideBar/manageClinics.svg';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

const useRoutes = () => {
    const { messages } = useContext(MessagesContext);
    const { pathname } = useLocation();
    const clinicId = pathname?.split('/').filter((item) => item)[0];
    const patient = useSelector((state) => state.patient.patient.profile);

    const mainRoutes = [
        {
            id: 1,
            path: '/:clinicId/patients',
            name: 'patients',
            tooltip: messages.listOfPatients,
            link: `/${clinicId}/patients`,
            icon: <img src={listOfPatientsIcon} alt="patients" />,
            component: PatientsHome
        },
        {
            id: 2,
            path: '/:clinicId/appointment/:type',
            name: 'appointments',
            tooltip: messages.sideRightCalenderTooltip,
            link: `/${clinicId}/appointment/day`,
            icon: <img src={calenderIcon} alt="appointments" />,
            component: CalenderOrAppointment,
            isMobile: false
        },
        {
            id: 3,
            path: '/:clinicId/search',
            name: 'search',
            tooltip: messages.search,
            link: `/${clinicId}/search`,
            icon: <img src={search} alt="search" />,
            component: AdvancedSearch,
            isMobile: false
        },
        {
            id: 4,
            path: '/:clinicId/statistics',
            name: 'statistics',
            tooltip: messages.statistics,
            link: `/${clinicId}/statistics`,
            icon: <img src={statistics} alt="statistics" />,
            component: Statistics,
            isMobile: false
        },
        {
            id: 5,
            path: '/:clinicId/patient/:id/:component',
            component: PatientRoutes,
            isSideBar: false
        },
        {
            id: 6,
            path: '/:clinicId/setting/:component',
            name: 'setting',
            tooltip: messages.setting,
            link: `/${clinicId}/setting`,
            icon: <img src={settingIcon} alt="settingIcon" />,
            component: SettingRoutes
        },
        {
            id: 7,
            path: '/:clinicId/profile',
            component: Profile,
            isMobile: true,
            isSideBar: false
        },
        {
            id: 8,
            path: '/:clinicId/add-patient',
            component: AddOrEditPatient,
            isMobile: true,
            isSideBar: false
        }
    ];

    const patientRoutes = [
        {
            id: 1,
            path: '/:clinicId/patient/:id/forms',
            link: `/${clinicId}/patient/${patient?.id}/forms`,
            label: messages.sidebar.forms,
            name: 'forms',
            icon: visitIcon,
            component: DynamicForms
        },
        {
            id: 2,
            path: '/:clinicId/patient/:id/laboratory',
            link: `/${clinicId}/patient/${patient?.id}/laboratory`,
            label: messages.sidebar.laboratory,
            name: 'laboratory',
            icon: laboratoryIcon,
            component: Laboratory
        },
        {
            id: 3,
            path: '/:clinicId/patient/:id/radiology',
            link: `/${clinicId}/patient/${patient?.id}/radiology`,
            label: messages.sidebar.radiology,
            name: 'radiology',
            icon: radiologyIcon,
            component: Radiology
        },
        {
            id: 4,
            path: '/:clinicId/patient/:id/medication',
            link: `/${clinicId}/patient/${patient?.id}/medication`,
            label: messages.sidebar.medication,
            name: 'medication',
            icon: drugsIcon,
            component: Drug
        },
        {
            id: 5,
            path: '/:clinicId/patient/:id/appointments',
            link: `/${clinicId}/patient/${patient?.id}/appointments`,
            label: messages.sidebar.appointment,
            name: 'appointments',
            icon: appoitmentIcon,
            component: Appointment,
            isMobile: false
        },
        {
            id: 6,
            path: '/:clinicId/patient/:id/profile',
            link: `/${clinicId}/patient/${patient?.id}/profile`,
            label: messages.sidebar.patientFile,
            name: 'profile',
            icon: profileIcon,
            component: File
        }
    ];
    const settingRoutes = [
        {
            id: 1,
            path: '/:clinicId/setting/manageForms',
            link: `/${clinicId}/setting/manageForms`,
            label: messages.permissionsTitle.manage_form,
            name: 'manageForms',
            icon: formsIcon,
            component: ManageForms,
            isMobile: false
        },
        {
            id: 3,
            path: '/:clinicId/setting/manageUsers',
            link: `/${clinicId}/setting/manageUsers`,
            label: messages.permissionsTitle.manage_user,
            name: 'manageUsers',
            icon: manageUsersIcon,
            component: ManageUsers,
            isMobile: false
        },
        {
            id: 4,
            path: '/:clinicId/setting/manageClinics',
            link: `/${clinicId}/setting/manageClinics`,
            label: messages.permissionsTitle.manage_clinic,
            name: 'manageClinics',
            icon: manageClinicsIcon,
            component: ManageClinics,
            isMobile: false
        }
    ];
    const allowedRoutes = (routes, access) => {
        return access?.length
            ? routes?.filter((item) => access?.find((val) => item.id === val))
            : null;
    };
    return {
        mainRoutes,
        patientRoutes,
        settingRoutes,
        allowedRoutes
    };
};
export default useRoutes;
